import { call, put, select, takeEvery, takeLeading } from 'redux-saga/effects';

import { selectTargetPlatform } from '@pressreader/appcore';
import { createEventChannel, dispatch, selectTyped } from '@pressreader/appstore';
import { onLogOutDoneEvent, sendLoginBeganEvent, sendLoginDoneEvent, sendLoginFailedEvent } from '@pressreader/authentication-api';
import {
    authenticationDone,
    authenticationErrorEvent,
    authenticationRequestAction,
    selectIsExternalAuthRedirectEnabled,
    selectUser,
} from '@pressreader/authentication-core';
import { AuthSubview } from '@pressreader/authentication-types';
import { navigateToAction, resetStateHistoryAction, selectNavConfig } from '@pressreader/navigation-core';
import { ViewName } from '@pressreader/navigation-types';
import { getBasePath } from '@pressreader/routing';
import { loadLegacyApp } from '@pressreader/src/app/shared/legacy/utils';
import { TargetPlatform } from '@pressreader/types';

import { loadExternalAuthenticationProvider } from 'nd.externalauthenticationprovider';
import ndLogger from 'nd.logger';
import { selectLegacyExternalAuthConfig, selectSignoutLocation } from 'app/common/core/selectors';
import { loadLegacyAuthentication } from 'app/shared/legacy/utils';

function* authenticationRequest(action: ReturnType<typeof authenticationRequestAction>) {
    const system = 'email';

    try {
        const user: ReturnType<typeof selectUser> = yield select(selectUser);

        if (user.isAuthenticated) {
            yield put(authenticationDone({ authenticationResult: user.isAuthenticated }));
        }

        // In pervasive (accessibility) mode the legacy in-page native sign-in modal isn't accessible.
        // When the host has NO external auth, route unauthorized users to the accessible /auth/signin
        // view instead — mirroring the pervasive Sign-in button (account.btn.tsx, #173646) — and resolve
        // the request negatively so callers like loginFlowSaga unwind cleanly. When external auth IS
        // configured, fall through to the legacy signIn() below, which redirects to the client's external
        // auth (accessible) per #173746; intercepting here would bypass that redirect and loop with the
        // signin sub-view's redirectToExternalAuthIfEnabled (which re-dispatches authenticationRequestAction).
        const platform = yield* selectTyped(selectTargetPlatform);
        const isExternalAuthRedirect = yield* selectTyped(selectIsExternalAuthRedirectEnabled);
        if (!user.isAuthenticated && platform === TargetPlatform.Pervasive && !isExternalAuthRedirect) {
            yield put(navigateToAction({ viewName: ViewName.Auth, params: { subview: AuthSubview.SignIn } }));
            yield put(authenticationDone({ authenticationResult: false }));
            return;
        }

        const legacyLoaded: ExtractReturnType<typeof loadLegacyApp> = yield loadLegacyApp();
        if (!legacyLoaded) {
            return;
        }

        const authentication = yield call(loadLegacyAuthentication);
        yield call(() => authentication.signIn());

        sendLoginBeganEvent({ system });
        // With external auth, the legacy signIn() above redirects the page to the client's auth (#173746).
        // In pervasive mode resolve the request negatively so in-flight callers (the favorites
        // loginFlowSaga) don't fire an authenticated request that 401s and surfaces a "No retries left."
        // alert during the redirect window.
        const isPervasiveExternalRedirect = !user.isAuthenticated && platform === TargetPlatform.Pervasive && isExternalAuthRedirect;
        yield put(authenticationDone({ authenticationResult: !isPervasiveExternalRedirect }));
        sendLoginDoneEvent({ system });
    } catch (error) {
        ndLogger.error(error);
        yield put(authenticationErrorEvent({ error }));
        yield put(authenticationDone({ authenticationResult: false }));
        sendLoginFailedEvent({ error, system });
    }
}

function* watchAuthEvents() {
    yield takeEvery(createEventChannel(onLogOutDoneEvent), function* () {
        const extAuthProvider = yield loadExternalAuthenticationProvider();
        const extAuthProviderSignoutUrl = typeof extAuthProvider?.signOut === 'function' ? extAuthProvider.signOut() : '';

        const signoutLocation = yield* selectTyped(selectSignoutLocation);
        const { externalLogoutPageDelay: redirectDelay = 500 } = yield* selectTyped(selectLegacyExternalAuthConfig);

        const navConfig = yield* selectTyped(selectNavConfig);
        const noViewRedirectUrl = !navConfig?.viewName ? getBasePath() : '';

        if (extAuthProviderSignoutUrl || signoutLocation || noViewRedirectUrl) {
            setTimeout(() => {
                window.location.assign(extAuthProviderSignoutUrl || signoutLocation || noViewRedirectUrl);
            }, redirectDelay);
            return;
        }

        dispatch(resetStateHistoryAction());
    });
}

function* saga() {
    yield* watchAuthEvents();

    yield takeLeading(authenticationRequestAction, authenticationRequest);
}

export { saga };
